/**
 * User Roles and Permissions for Smile Analyst
 *
 * Defines role-based access control (RBAC) for the application.
 */

export const USER_ROLES = {
  DOCTOR: 'doctor',
  STAFF: 'staff',
  ADMIN: 'admin'
};

export const ROLE_DISPLAY_NAMES = {
  [USER_ROLES.DOCTOR]: 'Doctor',
  [USER_ROLES.STAFF]: 'Staff',
  [USER_ROLES.ADMIN]: 'Administrator'
};

/**
 * Permissions for each role
 */
export const ROLE_PERMISSIONS = {
  [USER_ROLES.DOCTOR]: {
    // Patients
    viewPatients: true,
    createPatients: true,
    editPatients: true,
    deletePatients: true,

    // Cases
    viewCases: true,
    createCases: true,
    editCases: true,
    deleteCases: true,
    createVersions: true,

    // Photos
    viewPhotos: true,
    uploadPhotos: true,
    editPhotos: true,
    deletePhotos: true,
    downloadPhotos: true,

    // Users
    viewUsers: true,
    createUsers: false,
    editUsers: false,
    deleteUsers: false,

    // Practice Settings
    viewSettings: true,
    editSettings: false,

    // Audit Logs
    viewAuditLogs: false
  },

  [USER_ROLES.STAFF]: {
    // Patients
    viewPatients: true,
    createPatients: true,
    editPatients: true,
    deletePatients: false,

    // Cases
    viewCases: true,
    createCases: true,
    editCases: true,
    deleteCases: false,
    createVersions: true,

    // Photos
    viewPhotos: true,
    uploadPhotos: true,
    editPhotos: true,
    deletePhotos: false,
    downloadPhotos: true,

    // Users
    viewUsers: true,
    createUsers: false,
    editUsers: false,
    deleteUsers: false,

    // Practice Settings
    viewSettings: true,
    editSettings: false,

    // Audit Logs
    viewAuditLogs: false
  },

  [USER_ROLES.ADMIN]: {
    // Patients
    viewPatients: true,
    createPatients: true,
    editPatients: true,
    deletePatients: true,

    // Cases
    viewCases: true,
    createCases: true,
    editCases: true,
    deleteCases: true,
    createVersions: true,

    // Photos
    viewPhotos: true,
    uploadPhotos: true,
    editPhotos: true,
    deletePhotos: true,
    downloadPhotos: true,

    // Users
    viewUsers: true,
    createUsers: true,
    editUsers: true,
    deleteUsers: true,

    // Practice Settings
    viewSettings: true,
    editSettings: true,

    // Audit Logs
    viewAuditLogs: true
  }
};

/**
 * Check if a user has a specific permission
 * @param {string} role - User role
 * @param {string} permission - Permission to check
 * @returns {boolean} True if user has permission
 */
export const hasPermission = (role, permission) => {
  const permissions = ROLE_PERMISSIONS[role];
  return permissions ? permissions[permission] === true : false;
};

/**
 * Check if a user has any of the specified roles
 * @param {string} userRole - User's role
 * @param {Array<string>} allowedRoles - Array of allowed roles
 * @returns {boolean} True if user has one of the allowed roles
 */
export const hasRole = (userRole, allowedRoles) => {
  return allowedRoles.includes(userRole);
};

/**
 * Get all available roles
 * @returns {Array<Object>} Array of role objects
 */
export const getAllRoles = () => {
  return Object.entries(USER_ROLES).map(([key, value]) => ({
    key,
    value,
    displayName: ROLE_DISPLAY_NAMES[value]
  }));
};

/**
 * Get role display name
 * @param {string} role - Role value
 * @returns {string} Display name
 */
export const getRoleDisplayName = (role) => {
  return ROLE_DISPLAY_NAMES[role] || role;
};

/**
 * Check if role is valid
 * @param {string} role - Role to validate
 * @returns {boolean} True if role is valid
 */
export const isValidRole = (role) => {
  return Object.values(USER_ROLES).includes(role);
};

export default {
  USER_ROLES,
  ROLE_DISPLAY_NAMES,
  ROLE_PERMISSIONS,
  hasPermission,
  hasRole,
  getAllRoles,
  getRoleDisplayName,
  isValidRole
};
